import axios from 'axios';

export interface ApiError extends Error {
  code?: string;
  requestId?: string;
  status?: number;
}

export interface Invoice extends Record<string, unknown> {
  invoice_id?: number;
  app_id?: number;
  owner_code?: number;
  invoice_status?: string;
  invoice_date?: string;
  refund_date?: string | null;
  developer_payload?: string | null;
  purchase_id?: string;
  order?: {
    order_id?: string;
    order_number?: string | null;
    visual_name?: string;
    amount_create?: number;
    amount_current?: number;
    currency?: string;
    item_code?: string;
    description?: string;
    language?: string;
  };
  payment_info?: {
    payment_date?: string;
    masked_pan?: string;
    payment_system?: string;
    payment_way?: string;
    payment_way_code?: string;
    bank_name?: string;
  };
}

export interface InvoicesResponse {
  request_id: string;
  timestamp: string;
  code: string;
  message: string;
  data: {
    invoices: Invoice[];
    page: number;
    page_size: number;
    total_elements: number;
    total_pages: number;
  };
}

export interface ProductTariff {
  name?: string;
  priceCurrency?: string;
  priceValue?: number;
  tariffCode?: string;
}

export interface Product {
  description?: string;
  image?: string;
  name?: string;
  productId?: string;
  productType?: string;
  status?: string;
  tariffs?: ProductTariff[];
}

export interface ProductsBody {
  continuationToken?: string;
  elements: Product[];
  elementsCount: number;
  totalElementsCount: number;
}

export interface ProductsResponse {
  requestId: string;
  timestamp: string;
  code: string;
  message: string;
  data: ProductsBody | null;
}

export interface FetchInvoicesParams {
  appId: string;
  statuses: string[];
  date: string;
  page: number;
  pageSize: number;
  token: string;
}

export interface FetchProductsParams {
  appId: string;
  limit?: number;
  token: string;
}

export interface ConfirmPurchaseParams {
  appId: string;
  purchaseId: string;
  environment: 'production' | 'sandbox';
  token: string;
}

export interface ConfirmPurchaseResponse {
  request_id?: string;
  code: string;
  message: string;
  timestamp?: string;
}

export interface ExternalLinkRequest {
  productCode: string;
  quantity: number;
  orderId: string;
  developerPayload?: string;
  appUserId: string;
  platform: 'web' | 'mobile';
  backUrl?: string;
  successUrl?: string;
  failUrl?: string;
}

export interface ExternalLinkResponse {
  requestId: string;
  code: string;
  message: string;
  timestamp: string;
  data: {
    payUrl: string;
    purchaseId: string;
    invoiceId: number;
  };
}

export interface GetExternalLinkParams {
  appId: string;
  payload: ExternalLinkRequest;
  token: string;
}

const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const fetchInvoices = async ({
  appId,
  statuses,
  date,
  page,
  pageSize,
  token,
}: FetchInvoicesParams): Promise<InvoicesResponse> => {
  if (!appId) {
    const error = new Error('App ID is required to fetch invoices.');
    throw error;
  }

  if (!date) {
    const error = new Error('Date is required to fetch invoices.');
    throw error;
  }

  if (!statuses.length) {
    const error = new Error('Select at least one invoice status.');
    throw error;
  }

  const requestId = generateRequestId();

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  params.set('date', date);

  statuses.forEach((status) => {
    params.append('statuses[]', status);
  });

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endpoint = `${baseUrl}/api/applications/${appId}/invoices`;
  const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

  try {
    const response = await axios.get<InvoicesResponse>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Request-ID': requestId,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const payload = error.response?.data as
        | Partial<InvoicesResponse>
        | { error?: string; message?: string; code?: string; request_id?: string }
        | undefined;

      const message =
        payload?.message ||
        (typeof payload === 'object' && payload && 'error' in payload
          ? String(payload.error)
          : undefined) ||
        error.message ||
        'Failed to load invoices.';

      const enrichedError = new Error(message) as ApiError;
      enrichedError.code =
        payload && 'code' in payload && typeof payload.code === 'string'
          ? payload.code
          : error.response?.data?.code;
      enrichedError.requestId =
        payload && 'request_id' in payload && typeof payload.request_id === 'string'
          ? payload.request_id
          : undefined;
      enrichedError.status = error.response?.status;
      throw enrichedError;
    }

    throw error;
  }
};

export const fetchProducts = async ({
  appId,
  limit,
  token,
}: FetchProductsParams): Promise<ProductsResponse> => {
  if (!appId) {
    throw new Error('App ID is required to fetch products.');
  }

  if (!token) {
    throw new Error('Authentication token is required to fetch products.');
  }

  if (limit !== undefined && limit !== null && limit <= 0) {
    throw new Error('Limit must be greater than 0.');
  }

  const requestId = generateRequestId();
  const params = new URLSearchParams();
  if (limit) {
    params.set('limit', String(limit));
  }

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endpoint = `${baseUrl}/api/applications/${appId}/products`;
  const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

  try {
    const response = await axios.get<ProductsResponse>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Request-ID': requestId,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const payload = error.response?.data as
        | Partial<ProductsResponse>
        | { error?: string; message?: string; code?: string; requestId?: string; request_id?: string }
        | undefined;

      const message =
        payload?.message ||
        (typeof payload === 'object' && payload && 'error' in payload
          ? String(payload.error)
          : undefined) ||
        error.message ||
        'Failed to load products.';

      const enrichedError = new Error(message) as ApiError;
      enrichedError.code =
        payload && 'code' in payload && typeof payload.code === 'string'
          ? payload.code
          : error.response?.data?.code;
      enrichedError.requestId =
        payload && 'requestId' in payload && typeof payload.requestId === 'string'
          ? payload.requestId
          : payload && 'request_id' in payload && typeof payload.request_id === 'string'
            ? payload.request_id
            : undefined;
      enrichedError.status = error.response?.status;
      throw enrichedError;
    }

    throw error;
  }
};

export const confirmPurchase = async ({
  appId,
  purchaseId,
  environment,
  token,
}: ConfirmPurchaseParams): Promise<ConfirmPurchaseResponse> => {
  if (!appId || !purchaseId) {
    throw new Error('App ID and Purchase ID are required to confirm a purchase.');
  }

  if (!token) {
    throw new Error('Authentication token is required to confirm a purchase.');
  }

  const requestId = generateRequestId();
  const params = new URLSearchParams({ environment });
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endpoint = `${baseUrl}/api/purchases/${appId}/${purchaseId}/confirm`;
  const url = `${endpoint}?${params.toString()}`;

  try {
    const response = await axios.put<ConfirmPurchaseResponse>(
      url,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Request-ID': requestId,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const payload = error.response?.data as
        | Partial<ConfirmPurchaseResponse>
        | { error?: string; message?: string; code?: string; request_id?: string }
        | undefined;

      const message =
        payload?.message ||
        (typeof payload === 'object' && payload && 'error' in payload
          ? String(payload.error)
          : undefined) ||
        error.message ||
        'Failed to confirm purchase.';

      const enrichedError = new Error(message) as ApiError;
      enrichedError.code =
        payload && 'code' in payload && typeof payload.code === 'string'
          ? payload.code
          : error.response?.data?.code;
      enrichedError.requestId =
        payload && 'request_id' in payload && typeof payload.request_id === 'string'
          ? payload.request_id
          : undefined;
      enrichedError.status = error.response?.status;
      throw enrichedError;
    }

    throw error;
  }
};

export const getExternalLink = async ({
  appId,
  payload,
  token,
}: GetExternalLinkParams): Promise<ExternalLinkResponse> => {
  if (!appId) {
    throw new Error('App ID is required to get an external link.');
  }

  if (!token) {
    throw new Error('Authentication token is required to get an external link.');
  }

  const requestId = generateRequestId();
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endpoint = `${baseUrl}/api/applications/${appId}/external-link`;

  try {
    const response = await axios.post<ExternalLinkResponse>(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Request-ID': requestId,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const payloadResponse = error.response?.data as
        | Partial<ExternalLinkResponse>
        | { error?: string; message?: string; code?: string; requestId?: string; request_id?: string }
        | undefined;

      const message =
        payloadResponse?.message ||
        (typeof payloadResponse === 'object' && payloadResponse && 'error' in payloadResponse
          ? String(payloadResponse.error)
          : undefined) ||
        error.message ||
        'Failed to get external link.';

      const enrichedError = new Error(message) as ApiError;
      enrichedError.code =
        payloadResponse && 'code' in payloadResponse && typeof payloadResponse.code === 'string'
          ? payloadResponse.code
          : error.response?.data?.code;
      enrichedError.requestId =
        payloadResponse && 'requestId' in payloadResponse && typeof payloadResponse.requestId === 'string'
          ? payloadResponse.requestId
          : payloadResponse && 'request_id' in payloadResponse && typeof payloadResponse.request_id === 'string'
            ? payloadResponse.request_id
            : undefined;
      enrichedError.status = error.response?.status;
      throw enrichedError;
    }

    throw error;
  }
};

export const cancelPurchase = async ({
  appId,
  purchaseId,
  environment,
  token,
}: ConfirmPurchaseParams): Promise<ConfirmPurchaseResponse> => {
  if (!appId || !purchaseId) {
    throw new Error('App ID and Purchase ID are required to cancel a purchase.');
  }

  if (!token) {
    throw new Error('Authentication token is required to cancel a purchase.');
  }

  const requestId = generateRequestId();
  const params = new URLSearchParams({ environment });
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endpoint = `${baseUrl}/api/purchases/${appId}/${purchaseId}/cancel`;
  const url = `${endpoint}?${params.toString()}`;

  try {
    const response = await axios.put<ConfirmPurchaseResponse>(
      url,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Request-ID': requestId,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const payload = error.response?.data as
        | Partial<ConfirmPurchaseResponse>
        | { error?: string; message?: string; code?: string; request_id?: string }
        | undefined;

      const message =
        payload?.message ||
        (typeof payload === 'object' && payload && 'error' in payload
          ? String(payload.error)
          : undefined) ||
        error.message ||
        'Failed to cancel purchase.';

      const enrichedError = new Error(message) as ApiError;
      enrichedError.code =
        payload && 'code' in payload && typeof payload.code === 'string'
          ? payload.code
          : error.response?.data?.code;
      enrichedError.requestId =
        payload && 'request_id' in payload && typeof payload.request_id === 'string'
          ? payload.request_id
          : undefined;
      enrichedError.status = error.response?.status;
      throw enrichedError;
    }

    throw error;
  }
};
